import { CSSProperties } from "react"
import { ICalculatorState, Section } from "../Types/calculator"
import { CrashData } from "../Components/CrashData"
import { Fleet } from "../Components/Fleet"
import { Insurance } from "../Components/Insurance"
import { Pricing } from "../Components/Pricing"
import { Technologies } from "../Components/Technologies"
import { Training } from "../Components/Training"
import { getVariable } from "./calculations"

export const VERSION_NUMBER = "2.0.0-rc-4"

export const errorStyles: CSSProperties = { borderColor: "var(--bs-danger)", borderStyle: "normal", borderWidth: 2 }
export const DEFAULT_STATE: ICalculatorState = {
  technologies: [],
  costPerUnit: 0,
  annualFees: 0,
  activeCategory: "Technologies",
  driverTraining: "No",
  driverHourlyPay: 25,
  driverCoaching: "No",
  managerHourlyPay: 35,
  useDefaultCrashData: "Yes",
  numPropertyDamageCrashes: 0,
  numInjuryCrashes: 0,
  numFatalityCrashes: 0,
  crashMitigationEffectiveness: getVariable("Crash Mitigation Effectiveness Default"),
  pdoOutOfPocket: getVariable("Cost of PDO Crash"),
  injuryOutOfPocket: getVariable("Cost of Injury Crash"),
  fatalOutOfPocket: getVariable("Cost of Fatality Crash"),
  fleetMiles: 0,
  fleetSize: 0,
  calculatedCostPerUnit: 0,
  useCalculatedCostPerUnit: true,
  expandedSections: ["Technologies"],
  useMilesPerVehicle: false,
  useSimpleForm: true,
  versionNumber: VERSION_NUMBER
}

export const sections: Section[] = [
  {
    SectionComponent: Technologies,
    showOnSimpleForm: true,
    fields: {
      "technologies": {
        validationFunction: (value: ICalculatorState["technologies"]) => value.length > 0,
        outOfRangeMessage: "At least one technology must be selected."
      }
    },
    name: "Technologies"
  },
  {
    SectionComponent: Pricing,
    showOnSimpleForm: false,
    fields: {
      "useCalculatedCostPerUnit": {
        validationFunction: (value: ICalculatorState["useCalculatedCostPerUnit"]) => true,
        outOfRangeMessage: ""
      },
      "costPerUnit": {
        validationFunction: (value: ICalculatorState["costPerUnit"]) => value >= 0 && value <= 100000,
        outOfRangeMessage: "Cost per unit must be between 0 and 100,000."
      },
      "annualFees": {
        validationFunction: (value: ICalculatorState["annualFees"]) => value >= 0 && value <= 1000000,
        outOfRangeMessage: "Annual fees must be between 0 and 1,000,000."
      }
    },
    name: "Pricing"
  },
  {
    SectionComponent: Training,
    showOnSimpleForm: false,
    fields: {
      "driverTraining": {
        validationFunction: (value: ICalculatorState["driverTraining"]) => value === "Yes" || value === "No",
        outOfRangeMessage: ""
      },
      "driverHourlyPay": {
        validationFunction: (value: ICalculatorState["driverHourlyPay"]) => value >= 0 && value <= 200,
        outOfRangeMessage: "Driver hourly pay must be between 0 and 200."
      },
      "driverCoaching": {
        validationFunction: (value: ICalculatorState["driverCoaching"]) => value === "Yes" || value === "No",
        outOfRangeMessage: ""
      },
      "managerHourlyPay": {
        validationFunction: (value: ICalculatorState["managerHourlyPay"]) => value >= 0 && value <= 200,
        outOfRangeMessage: "Manager hourly pay must be between 0 and 200."
      }
    },
    name: "Training and Coaching"
  },
  {
    SectionComponent: Fleet,
    showOnSimpleForm: true,
    fields: {
      "fleetSize": {
        validationFunction: (value: ICalculatorState["fleetSize"]) => value > 0 && value <= 100000,
        outOfRangeMessage: "Fleet size must be between 0 and 100,000."
      },
      "fleetMiles": {
        validationFunction: (value: ICalculatorState["fleetMiles"], state?: ICalculatorState) => value > 0 && value <= (state?.useMilesPerVehicle ? 150000 : (state?.fleetSize || 1) * 150000),
        outOfRangeMessage: "Fleet miles must be between 0 and 150,000 per vehicle."
      },
      "useMilesPerVehicle": {
        validationFunction: (value: ICalculatorState["useMilesPerVehicle"]) => true,
        outOfRangeMessage: ""
      }
    },
    name: "Fleet"
  },
  {
    SectionComponent: Insurance,
    showOnSimpleForm: false,
    fields: {
      "pdoOutOfPocket": {
        validationFunction: (value: ICalculatorState["pdoOutOfPocket"]) => value >= 0 && value <= 1_000_000,
        outOfRangeMessage: "PDO out of pocket must be between 0 and 1,000,000."
      },
      "injuryOutOfPocket": {
        validationFunction: (value: ICalculatorState["injuryOutOfPocket"]) => value >= 0 && value <= 5_000_000,
        outOfRangeMessage: "Injury out of pocket must be between 0 and 5,000,000."
      },
      "fatalOutOfPocket": {
        validationFunction: (value: ICalculatorState["fatalOutOfPocket"]) => value >= 0 && value <= 10_000_000,
        outOfRangeMessage: "Fatal out of pocket must be between 0 and 10,000,000."
      }
    },
    name: "Insurance"
  },
  {
    SectionComponent: CrashData,
    showOnSimpleForm: false,
    fields: {
      "numPropertyDamageCrashes": {
        validationFunction: (value: ICalculatorState["numPropertyDamageCrashes"]) => value >= 0 && value <= 10_000,
        outOfRangeMessage: "Number of property damage crashes must be between 0 and 10,000."
      },
      "numInjuryCrashes": {
        validationFunction: (value: ICalculatorState["numInjuryCrashes"]) => value >= 0 && value <= 10_000,
        outOfRangeMessage: "Number of injury crashes must be between 0 and 10,000."
      },
      "numFatalityCrashes": {
        validationFunction: (value: ICalculatorState["numFatalityCrashes"]) => value >= 0 && value <= 10_000,
        outOfRangeMessage: "Number of fatality crashes must be between 0 and 10,000."
      },
      "useDefaultCrashData": {
        validationFunction: (value: ICalculatorState["useDefaultCrashData"]) => value === "Yes" || value === "No",
        outOfRangeMessage: ""
      }
    },
    name: "Crash Data"
  }
]

export const NUMBER_OF_ADVANCED_SECTIONS = sections.length
export const NUMBER_OF_SIMPLE_SECTIONS = sections.filter(section => section.showOnSimpleForm).length

export const validationFunctions = Object.fromEntries(
  sections.flatMap(
    section => Object.entries(section.fields).map(
      ([field, { validationFunction }]) => [field, validationFunction]
    )
  )
) as Record<keyof ICalculatorState, (value: any, state?: ICalculatorState) => boolean>

export function allInputsValid(state: ICalculatorState): boolean {
  for (const key in state) {
    const section = sections.find(section => section.fields[key as keyof ICalculatorState] !== undefined)
    const validator = validationFunctions[key as keyof ICalculatorState]
    if (!validator || (section?.name && !state.expandedSections.includes(section?.name))) continue
    if (!validator(state[key as keyof ICalculatorState], state)) return false
  }
  return true
}

export function isFormComplete(state: ICalculatorState): boolean {
  return allInputsValid(state) &&
  state.expandedSections.filter(Boolean).length === (state.useSimpleForm ? NUMBER_OF_SIMPLE_SECTIONS : NUMBER_OF_ADVANCED_SECTIONS)
}

export function isSectionComplete(state: ICalculatorState, section: Section): boolean {
  if (!state.expandedSections.includes(section.name)) return false
  for (const [field, { validationFunction }] of Object.entries(section.fields)) {
    if (!validationFunction || !validationFunction(state[field as keyof ICalculatorState], state)) {
      return false
    }
  }
  return true
}

export const cleanNumberInput = (input: string): number => {
  const cleanedInput = input.replace(/,/g, '');
  const parts = cleanedInput.split('.');
  if (parts.length === 2 && parts[1].length > 2) {
    return Number(parts[0] + '.' + parts[1].slice(0, 2));
  }
  return Number(cleanedInput);
}

export function getTwoDigitLocaleString(number: number) {
  return number.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })
}